/**
 * Shared helper for the "let KaTeX text wrap" sweep.
 *
 * KaTeX renders every top-level run into a ``.base`` that is
 * ``display: inline-block; white-space: nowrap; width: min-content``
 * (katex.css). That shrink-wraps, so a long ELA sentence written as
 * ``$\textbf{...}$`` overflows its box instead of breaking. The app's answer is
 * the global ``.katex-html .wrap`` rule (global/styles.css), applied from JS
 * because CSS alone cannot tell which bases should reflow.
 *
 * The catch, and the reason this lives in one place now: some bases must NOT be
 * re-flowed. An ``array`` / ``matrix`` / ``cases`` environment compiles to an
 * ``.mtable`` of ``.vlist`` columns whose rows sit at offsets computed at render
 * time, on the assumption that the enclosing base keeps its own geometry. Force
 * that base inline and the rows wrap while their offsets stay put, so they land
 * on top of each other — which is exactly what SHSAT verse passages did, being
 * stored as ``$\begin{array}{rl} ... \end{array}$``.
 * See docs/bugfix/20260919-shsat-array-passage-wrap-overlap-plan.md.
 *
 * ``.mtable`` is deliberately narrower than ``.vlist-t``: superscripts and
 * fractions are vlists too, and a sentence containing a footnote marker should
 * still be allowed to wrap.
 */

/** Selector for KaTeX constructs whose row geometry is precomputed. */
export const KATEX_LAYOUT_SELECTOR = '.mtable';

/**
 * The slice of ``Element`` this module needs. Narrow on purpose: it keeps the
 * decision below unit-testable without a DOM (see katexWrap.test.ts).
 */
export interface KatexBase {
    querySelector(selectors: string): unknown;
}

/** True when the base holds layout KaTeX cannot reflow. */
export const isLayoutBase = (base: KatexBase): boolean => (
    base.querySelector(KATEX_LAYOUT_SELECTOR) != null
);

/**
 * Whether this base may wrap. ``enabled`` is the caller's own subject test —
 * each screen decides for itself whether its section wraps at all — and the
 * layout guard then vetoes the bases that would break.
 */
export const shouldWrapBase = (base: KatexBase, enabled: boolean): boolean => (
    enabled && !isLayoutBase(base)
);

/**
 * Add/remove ``wrap`` on every KaTeX base under ``root``.
 *
 * ``root`` is whatever the caller scopes to — ``document`` for the screens that
 * sweep the whole page (passage + question), or a ref for the ones that only
 * own a question column. A missing root is a no-op: callers run this from
 * effects that can fire before their ref attaches.
 */
export const applyKatexWrap = (
    root: ParentNode | null | undefined,
    { enabled }: { enabled: boolean },
): void => {
    if (!root) return;

    root.querySelectorAll('.katex-html .base').forEach((base) => {
        if (shouldWrapBase(base, enabled)) {
            base.classList.add('wrap');
        } else {
            base.classList.remove('wrap');
        }
    });
};

/**
 * KaTeX text-style classes worth carrying through a flatten. ``\textcolor`` is
 * an inline style, not a class, and is intentionally not preserved.
 */
const KATEX_TEXT_STYLE_CLASSES = ['textit', 'textbf', 'texttt', 'textsf'] as const;

/**
 * Math layout nested inside an underline: a vlist inside the underline's own
 * vlist (fraction, super/subscript, root) or a table. Its rows sit at offsets
 * computed at render time, so flattening it to text would destroy it.
 */
const KATEX_NESTED_LAYOUT_SELECTOR = '.vlist .vlist-t, .mtable';

/**
 * KaTeX's spacing accent glyphs for text-mode accents (``\'``, ``\^``, ...,
 * and precomposed letters such as ``é``, which KaTeX decomposes), mapped to
 * the combining mark that draws the same accent over the preceding letter.
 * Math accents (``\hat``, ``\vec``) use other glyphs and are not listed, so
 * they keep their layout.
 */
const COMBINING_ACCENTS: Readonly<Record<string, string>> = {
    'ˊ': '́', // ˊ acute
    'ˋ': '̀', // ˋ grave
    'ˆ': '̂', // ˆ circumflex
    '˜': '̃', // ˜ tilde
    'ˉ': '̄', // ˉ macron
    '˘': '̆', // ˘ breve
    '˙': '̇', // ˙ dot above
    '¨': '̈', // ¨ diaeresis
    '˚': '̊', // ˚ ring
    '˝': '̋', // ˝ double acute
    'ˇ': '̌', // ˇ caron
    '¸': '̧', // ¸ cedilla
};

/** Marks an underline the sweep has already rebuilt, so a re-run is a no-op. */
const FLATTENED_ATTR = 'data-katex-flattened';

/** The text-style classes between ``node`` and ``stop`` (exclusive), in a stable order. */
const textStylesBetween = (node: Node, stop: Element): string[] => {
    const found = new Set<string>();
    for (let el = node.parentElement; el && el !== stop; el = el.parentElement) {
        KATEX_TEXT_STYLE_CLASSES.forEach((cls) => {
            if (el!.classList.contains(cls)) found.add(cls);
        });
    }
    return KATEX_TEXT_STYLE_CLASSES.filter((cls) => found.has(cls));
};

/**
 * Rewrite each text-mode accented letter under ``underline`` from KaTeX's
 * vlist (base letter + separately positioned accent glyph) to plain text: the
 * base letter followed by the matching combining mark.
 *
 * Without this, the vlist matches ``KATEX_NESTED_LAYOUT_SELECTOR`` and an
 * underlined sentence containing "é" is never flattened, so it cannot wrap
 * (docs/bugfix/20261019-prestige-accent-overlap-and-passage-typos-plan.md).
 * The combining form keeps the character count — "e" + "ˊ" becomes "e" +
 * U+0301 — so highlight offsets do not move. Any ``.vlist-s`` spacer inside
 * the accent stays in the text, and the flatten moves it to the end as usual.
 *
 * Only an accent inside a ``\text`` run of this underline, with a known glyph
 * and a single base letter in a text font, is rewritten. A math accent such as
 * ``$\dot{x}$`` inside ``\text`` shares the glyph but its base is in a math
 * font, so it is left alone.
 *
 * All or nothing: the accents are rewritten only when that leaves no other
 * math layout in the underline, i.e. only when the underline is about to be
 * flattened. Otherwise the underline keeps KaTeX's native rendering untouched.
 */
const inlineTextAccents = (underline: HTMLElement): void => {
    const ready = Array.from(underline.querySelectorAll<HTMLElement>('.accent'))
        .map(textAccentRewrite)
        .filter((r): r is AccentRewrite => r !== null);
    if (!ready.length) return;

    const otherLayout = Array.from(underline.querySelectorAll(KATEX_NESTED_LAYOUT_SELECTOR))
        .some((el) => !ready.some(({ accent }) => accent.contains(el)));
    if (otherLayout) return;

    ready.forEach(({ accent, replacement }) => accent.parentNode?.replaceChild(replacement, accent));
};

interface AccentRewrite { accent: HTMLElement; replacement: HTMLElement }

/** KaTeX math-font classes. A base in one of these is a math accent. */
const KATEX_MATH_FONT_SELECTOR = '.mathnormal, .mathit, .mathbf, .boldsymbol, .mathsf, .mathtt, .mathrm';

/** The plain-text replacement for one accent, or null to leave it as is. */
const textAccentRewrite = (accent: HTMLElement): AccentRewrite | null => {
    const doc = accent.ownerDocument;
    if (!accent.closest('.text')) return null;

    const body = accent.querySelector('.accent-body');
    const combining = COMBINING_ACCENTS[body?.textContent ?? ''];
    if (!body || !combining) return null;

    const walker = doc.createTreeWalker(accent, NodeFilter.SHOW_TEXT);
    const baseNodes: Node[] = [];
    let spacers = '';
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (body.contains(node)) continue;
        const value = node.nodeValue ?? '';
        spacers += value.replace(/[^\u200B]/g, '');
        if (value.replace(/\u200B/g, '')) baseNodes.push(node);
    }
    if (baseNodes.length !== 1) return null;
    const base = (baseNodes[0].nodeValue ?? '').replace(/\u200B/g, '');
    if (Array.from(base).length !== 1) return null;
    const mathFont = baseNodes[0].parentElement?.closest(KATEX_MATH_FONT_SELECTOR);
    if (mathFont && accent.contains(mathFont)) return null;

    const replacement = doc.createElement('span');
    replacement.className = ['mord', ...textStylesBetween(baseNodes[0], accent)].join(' ');
    replacement.textContent = base + combining + spacers;
    return { accent, replacement };
};

/**
 * Flatten every KaTeX ``\underline`` under ``root`` to wrappable text, keeping
 * italic / bold / mono / sans.
 *
 * KaTeX draws an underline as a vlist whose rows sit at offsets computed at
 * render time. Once ``applyKatexWrap`` has made the base wrappable, a long
 * underlined run wraps while its rows keep their offsets, and the lines paint
 * on top of each other. Replacing the vlist with its text lets the run wrap
 * like any other text; the global ``.katex-html .underline`` rule then draws
 * the line with ``text-decoration``.
 *
 * The earlier form of this sweep (``el.innerHTML = el.innerText``, once copied
 * into every screen) threw away the ``.textit`` / ``.textbf`` spans KaTeX puts
 * inside the underline, so ``\underline{\textit{...}}`` rendered upright. This
 * version walks the text nodes and rebuilds them as runs, each wrapped in a
 * span carrying the style classes it had inside the underline. KaTeX's own
 * stylesheet (``.katex .textit`` etc.) styles those spans, so no app CSS is
 * needed. See docs/bugfix/20261006-katex-underline-styles-plan.md.
 *
 * Left alone: a run holding a ``<mark>`` (a highlight some other code owns),
 * one already rebuilt, and one holding math layout (see
 * ``KATEX_NESTED_LAYOUT_SELECTOR``), which keeps KaTeX's native rendering.
 * Accented letters are not math layout: ``inlineTextAccents`` turns them into
 * text first, so an underline containing "é" still flattens.
 *
 * Text is read from the nodes, not ``innerText``, so the result does not
 * depend on layout and is identical in a hidden overlay; the ``.vlist-s``
 * zero-width spacer is moved to the end (kept, so the run's character count,
 * which passage highlight offsets index, is unchanged) and KaTeX's
 * non-breaking spaces become plain spaces so the run can wrap. Nodes are
 * built with DOM calls, never ``innerHTML``, so a ``<`` or ``&`` KaTeX
 * escaped stays literal.
 *
 * Selectors assume KaTeX 0.16.x class names (``.base``, ``.underline``);
 * KaTeX 0.19 renames both (``.katex-base``, ``.katex-underline``).
 */
export const flattenKatexUnderlines = (root: ParentNode | null | undefined): void => {
    if (!root) return;

    root.querySelectorAll<HTMLElement>('.katex-html .base .underline').forEach((underline) => {
        if (underline.querySelector('mark')) return;
        if (underline.hasAttribute(FLATTENED_ATTR)) return;
        inlineTextAccents(underline);
        if (underline.querySelector(KATEX_NESTED_LAYOUT_SELECTOR)) return;

        const doc = underline.ownerDocument;
        const walker = doc.createTreeWalker(underline, NodeFilter.SHOW_TEXT);
        const runs: { key: string; text: string }[] = [];
        let spacers = 0;
        for (let node = walker.nextNode(); node; node = walker.nextNode()) {
            const value = node.nodeValue ?? '';
            const text = value.replace(/\u200B/g, '').replace(/\u00A0/g, ' ');
            spacers += value.length - value.replace(/\u200B/g, '').length;
            if (!text) continue;
            const key = textStylesBetween(node, underline).join(' ');
            const last = runs[runs.length - 1];
            if (last && last.key === key) {
                last.text += text;
            } else {
                runs.push({ key, text });
            }
        }

        const nodes = runs.map(({ key, text }) => {
            if (!key) return doc.createTextNode(text);
            const span = doc.createElement('span');
            span.className = key;
            span.textContent = text;
            return span;
        });
        // Clear-and-append rather than replaceChildren: client is on TS 4.7 and
        // no app pins a browserslist.
        underline.textContent = '';
        nodes.forEach((node) => underline.appendChild(node));
        // Put KaTeX's zero-width spacer back, once per character removed above, as
        // a trailing node (where KaTeX itself places it). It is invisible and does
        // not affect wrapping, but passage offsets count rendered characters
        // INCLUDING it: every Digital ACT workbook's `Highlight Index` and figure
        // anchor was authored against KaTeX's output, and the highlight walk
        // (DigitalACT/Module generateHightlightIndexing) sums text-node lengths.
        // Dropping it shifted every highlight one character right per underline
        // above it in the passage.
        if (spacers) underline.appendChild(doc.createTextNode('\u200B'.repeat(spacers)));
        underline.setAttribute(FLATTENED_ATTR, '');
    });
};
